import {
  collection,
  doc,
  QueryDocumentSnapshot,
  Timestamp,
} from "firebase/firestore";
import {
  FirestoreManuscriptProcessingReporting,
  ManuscriptProcessingError,
  ManuscriptProcessingStatus as ContractStatus,
} from "reedy-data/models";

import { db } from "integrations/firebase/firestore";

export const MANUSCRIPT_PROCESSING_REPORTING_COLLECTION =
  "manuscriptProcessingReporting";

export type ManuscriptProcessingStatus = `${ContractStatus}`;

// Lifecycle order, mirrored by the panel status tiles.
export const MANUSCRIPT_PROCESSING_STATUSES = [
  ContractStatus.MATCHED,
  ContractStatus.QUEUED,
  ContractStatus.UPLOADING,
  ContractStatus.PROCESSING,
  ContractStatus.COMPLETE,
  ContractStatus.ERROR,
  ContractStatus.SKIPPED_DUPLICATE,
  ContractStatus.UNMATCHED,
] as const satisfies readonly ManuscriptProcessingStatus[];

export type { ManuscriptProcessingError };

// Diverges from the contract on purpose: status stays a string so a state from
// a newer backend still renders, and the converter validates nothing.
export type ManuscriptProcessingReportWithId = Pick<
  FirestoreManuscriptProcessingReporting,
  "id" | "organisation"
> &
  Partial<
    Omit<
      FirestoreManuscriptProcessingReporting,
      "id" | "organisation" | "status"
    >
  > & { status: string };

type FirestoreReport = Omit<
  ManuscriptProcessingReportWithId,
  "id" | "createdAt" | "updatedAt"
> & {
  createdAt?: Timestamp | Date | string | null;
  updatedAt?: Timestamp | Date | string | null;
};

const toDate = (value: Timestamp | Date | string | null | undefined) => {
  if (!value) return undefined;
  if (value instanceof Date) return value;
  if (typeof value === "string") return new Date(value);
  return value.toDate();
};

const converter = {
  toFirestore: (report: ManuscriptProcessingReportWithId) => {
    // biome-ignore lint/correctness/noUnusedVariables: drops id before the document is written to Firestore
    const { id, ...rest } = report;
    return rest;
  },
  fromFirestore(
    snapshot: QueryDocumentSnapshot<FirestoreReport>,
  ): ManuscriptProcessingReportWithId {
    const data = snapshot.data();
    return {
      ...data,
      createdAt: toDate(data.createdAt),
      updatedAt: toDate(data.updatedAt),
      id: snapshot.id,
    };
  },
};

export const getManuscriptProcessingReportingCollection = () =>
  collection(db, MANUSCRIPT_PROCESSING_REPORTING_COLLECTION).withConverter(
    converter,
  );

export const getManuscriptProcessingReportDocument = (reportId: string) =>
  doc(getManuscriptProcessingReportingCollection(), reportId);
